/** Shared edge tolerance for horizontal scroll (subpixels + wheel handoff to vertical page scroll). */
export const HORIZONTAL_SCROLL_EDGE_TOLERANCE = 1;

/** Wider tolerance for fade / arrow disabled state when browser stops before theoretical max. */
export const HORIZONTAL_SCROLL_VISUAL_EDGE_TOLERANCE = 2;

/** Минимальный overflow в px после которого у контейнера появляется скролл. */
export const HORIZONTAL_SCROLL_OVERFLOW_THRESHOLD = 5;

export const hasHorizontalScrollOverflow = (
  scrollWidth: number,
  clientWidth: number,
  threshold = HORIZONTAL_SCROLL_OVERFLOW_THRESHOLD
): boolean => scrollWidth - clientWidth > threshold;

export const getMaxScrollLeft = (
  scrollWidth: number,
  clientWidth: number
): number => Math.max(0, scrollWidth - clientWidth);

export const canScrollHorizontallyLeft = (scrollLeft: number): boolean =>
  scrollLeft > HORIZONTAL_SCROLL_EDGE_TOLERANCE;

export const canScrollHorizontallyRight = (
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number
): boolean =>
  !isAtHorizontalScrollVisualEnd(scrollLeft, scrollWidth, clientWidth);

export const isAtHorizontalScrollEnd = (
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number
): boolean =>
  scrollLeft >=
  getMaxScrollLeft(scrollWidth, clientWidth) - HORIZONTAL_SCROLL_EDGE_TOLERANCE;

export const isAtHorizontalScrollVisualEnd = (
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number
): boolean =>
  scrollLeft >=
  getMaxScrollLeft(scrollWidth, clientWidth) -
    HORIZONTAL_SCROLL_VISUAL_EDGE_TOLERANCE;

export const isAtHorizontalScrollStart = (scrollLeft: number): boolean =>
  scrollLeft <= HORIZONTAL_SCROLL_EDGE_TOLERANCE;

/** DOM probe: browser cannot scroll further right (subpixel max). */
export const isAtPhysicalMaxScroll = (element: HTMLElement): boolean => {
  const probeScrollLeft = element.scrollLeft;
  element.scrollLeft = probeScrollLeft + 1;
  const atMax = element.scrollLeft <= probeScrollLeft;
  element.scrollLeft = probeScrollLeft;
  return atMax;
};

export const clampHorizontalScrollLeft = (
  value: number,
  scrollWidth: number,
  clientWidth: number
): number =>
  Math.min(Math.max(0, value), getMaxScrollLeft(scrollWidth, clientWidth));

export const snapHorizontalScrollLeftToEdge = (
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number
): number => {
  const maxScrollLeft = getMaxScrollLeft(scrollWidth, clientWidth);

  if (scrollLeft >= maxScrollLeft - HORIZONTAL_SCROLL_EDGE_TOLERANCE) {
    return maxScrollLeft;
  }

  if (scrollLeft <= HORIZONTAL_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }

  return scrollLeft;
};

export const isHorizontalFadeHidden = (
  scrollLeft: number,
  scrollWidth: number,
  offsetWidth: number,
  clientWidth: number
): boolean =>
  scrollLeft + offsetWidth >= scrollWidth - HORIZONTAL_SCROLL_EDGE_TOLERANCE ||
  isAtHorizontalScrollVisualEnd(scrollLeft, scrollWidth, clientWidth);
